import React, { useState } from 'react';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';
import { InternshipResult } from '../../../../types/internship';
import { useInternshipResults } from '../../../../hooks/useInternshipResults';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { useToast } from '../../../../context/ToastContext';
import WizardProgress from './WizardProgress';
import OutcomeSelector from './OutcomeSelector';
import OfferDetailsForm from './OfferDetailsForm';
import NoOfferForm from './NoOfferForm';
import WithdrewForm from './WithdrewForm';
import ReflectionForm from './ReflectionForm';
import DocumentUpload from './DocumentUpload';

interface OutcomeWizardProps {
  focus: CareerFocusWithMetrics;
  existingResult?: InternshipResult;
  onComplete: () => void;
  onCancel: () => void;
}

interface WizardStep {
  id: string;
  label: string;
  component: React.ComponentType<StepProps>;
}

interface StepProps {
  data: Partial<InternshipResult>;
  onUpdate: (updates: Partial<InternshipResult>) => void;
  onNext: () => void;
  onBack: () => void;
  focus: CareerFocusWithMetrics;
}

const OutcomeWizard: React.FC<OutcomeWizardProps> = ({
  focus,
  existingResult,
  onComplete,
  onCancel
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { createResult, updateResult } = useInternshipResults(focus.id);
  
  // Initialize wizard data with existing result or defaults
  const [wizardData, setWizardData] = useState<Partial<InternshipResult>>(
    existingResult || {
      focusId: focus.id,
      outcomeType: undefined,
      outcomeDate: new Date().toISOString()
    }
  );
  
  // Determine initial step based on existing data
  const getInitialStep = () => {
    if (!existingResult) return 0;
    
    // We need to build the steps first to get accurate indices
    const tempSteps = getStepsForOutcome(existingResult.outcomeType);
    
    // If we have reflection data, find the reflection step index
    if (existingResult.achievementsSummary || existingResult.lessonsLearned) {
      const reflectionIndex = tempSteps.findIndex(s => s.id === 'reflection');
      if (reflectionIndex >= 0) return reflectionIndex;
    }
    
    // If we have outcome-specific data, start at the details step
    if (existingResult.outcomeType === 'offer_received' && existingResult.offerDetails) {
      return 1; // Offer details step
    }
    if (existingResult.outcomeType === 'no_offer' && existingResult.feedbackReceived) {
      return 1; // Feedback step
    }
    if (existingResult.outcomeType === 'withdrew' && existingResult.rejectionReason) {
      return 1; // Reason step (using rejectionReason for withdrew case)
    }
    
    // Otherwise start at outcome selection
    return 0;
  };
  
  // Helper function to get steps for a specific outcome type
  const getStepsForOutcome = (outcomeType?: string): WizardStep[] => {
    const baseSteps: WizardStep[] = [
      { id: 'outcome', label: 'Outcome', component: OutcomeSelector }
    ];
    
    if (outcomeType) {
      switch (outcomeType) {
        case 'offer_received':
          baseSteps.push(
            { id: 'details', label: 'Offer Details', component: OfferDetailsForm }
          );
          break;
        case 'no_offer':
          baseSteps.push(
            { id: 'feedback', label: 'Feedback', component: NoOfferForm }
          );
          break;
        case 'withdrew':
          baseSteps.push(
            { id: 'reason', label: 'Reason', component: WithdrewForm }
          );
          break;
      }
      
      baseSteps.push(
        { id: 'reflection', label: 'Reflection', component: ReflectionForm },
        { id: 'documents', label: 'Documents', component: DocumentUpload }
      );
    }
    
    return baseSteps;
  };
  
  const [currentStep, setCurrentStep] = useState(getInitialStep());
  const [isSaving, setIsSaving] = useState(false);
  
  // Dynamic steps based on outcome type
  const getSteps = (): WizardStep[] => {
    return getStepsForOutcome(wizardData.outcomeType);
  };
  
  const steps = getSteps();
  const CurrentStepComponent = steps[currentStep]?.component;
  
  const handleUpdate = (updates: Partial<InternshipResult>) => {
    setWizardData(prev => {
      const newData = { ...prev, ...updates };
      
      // Ensure offer_details is properly set based on outcome type
      if (updates.outcomeType) {
        if (updates.outcomeType === 'offer_received') {
          // Initialize offer_details as empty object if not present
          newData.offerDetails = newData.offerDetails || {};
        } else {
          // Remove offer_details for non-offer outcomes
          newData.offerDetails = undefined;
        }
      }
      
      return newData;
    });
  };
  
  const handleNext = async () => {
    // For first step, create the result before moving forward
    if (!existingResult && currentStep === 0 && !wizardData.id) {
      // Ensure outcomeType is set before creating
      if (!wizardData.outcomeType) {
        showToast('Please select an outcome type', 'error');
        return;
      }
      
      setIsSaving(true);
      try {
        // Prepare data with proper offer_details handling
        const createData = { ...wizardData };
        
        // Ensure offer_details constraint is satisfied
        if (createData.outcomeType === 'offer_received') {
          createData.offerDetails = createData.offerDetails || {};
        } else {
          createData.offerDetails = undefined;
        }
        
        const result = await createResult(createData);
        if (result) {
          setWizardData(prev => ({ ...prev, id: result.id }));
          // Move to next step after successful creation
          setCurrentStep(prev => prev + 1);
        }
      } catch (error) {
        showToast('Failed to save progress', 'error');
        console.error('Error creating result:', error);
        return; // Don't advance on error
      } finally {
        setIsSaving(false);
      }
    } else if (currentStep >= steps.length - 1) {
      // We're on the last step - complete the wizard
      handleComplete();
      return;
    } else {
      // For other steps, save current state then advance
      if (wizardData.id) {
        setIsSaving(true);
        try {
          // Only send fields that have been set/changed
          const updateData: Partial<InternshipResult> = { id: wizardData.id };
          
          // Only include fields that are defined
          if (wizardData.outcomeType !== undefined) {
            updateData.outcomeType = wizardData.outcomeType;
          }
          
          // Handle offer_details based on outcome type
          if (wizardData.outcomeType === 'offer_received') {
            updateData.offerDetails = wizardData.offerDetails || {};
          } else if (wizardData.outcomeType !== 'offer_received' && wizardData.outcomeType !== undefined) {
            updateData.offerDetails = undefined;
          }
          
          // Include other fields only if they're defined
          const fieldsToCheck: (keyof InternshipResult)[] = [
            'decisionStatus', 'decisionDate', 'decisionDeadline', 'decisionFactors',
            'feedbackReceived', 'rejectionStage', 'rejectionReason', 
            'lessonsLearned', 'achievementsSummary', 'wouldRecommend'
          ];
          
          fieldsToCheck.forEach(field => {
            if (wizardData[field] !== undefined) {
              (updateData as any)[field] = wizardData[field];
            }
          });
          
          await updateResult(updateData);
        } catch (error) {
          showToast('Failed to save progress', 'error');
          console.error('Error updating result:', error);
        } finally {
          setIsSaving(false);
        }
      }
      // Always advance to next step
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleComplete = async () => {
    // Ensure we have an ID before completing
    if (!wizardData.id) {
      showToast('Please complete all steps before finalizing', 'error');
      return;
    }
    
    setIsSaving(true);
    try {
      await updateResult({ ...wizardData, isFinal: true });
      showToast('Results saved successfully!', 'success');
      onComplete();
    } catch (error) {
      showToast('Failed to save results', 'error');
      console.error('Error saving final results:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="p-6">
      <WizardProgress
        steps={steps.map(s => s.label)}
        currentStep={currentStep}
      />
      
      <div className="mt-8">
        {CurrentStepComponent && (
          <CurrentStepComponent
            data={wizardData}
            onUpdate={handleUpdate}
            onNext={handleNext}
            onBack={handleBack}
            focus={focus}
          />
        )}
      </div>
      
      {/* Footer Actions */}
      <div className="mt-8 flex items-center justify-between border-t pt-6">
        <button
          onClick={async () => {
            // Save any pending changes before exiting
            if (wizardData.id) {
              setIsSaving(true);
              try {
                await updateResult(wizardData);
                showToast('Progress saved', 'success');
              } catch (error) {
                showToast('Failed to save changes', 'error');
                console.error('Error saving on exit:', error);
              } finally {
                setIsSaving(false);
              }
            }
            onCancel();
          }}
          disabled={isSaving}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            darkMode
              ? 'text-gray-400 hover:text-gray-300 disabled:text-gray-600'
              : 'text-gray-600 hover:text-gray-700 disabled:text-gray-400'
          } disabled:cursor-not-allowed`}
        >
          {isSaving ? 'Saving...' : 'Save & Exit'}
        </button>
        
        <div className="flex items-center gap-3">
          {currentStep > 0 && (
            <button
              onClick={handleBack}
              disabled={isSaving}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              } disabled:opacity-50`}
            >
              Back
            </button>
          )}
          
          <button
            onClick={handleNext}
            disabled={isSaving || !wizardData.outcomeType}
            className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
              darkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            } disabled:opacity-50`}
          >
            {isSaving ? 'Saving...' : currentStep === steps.length - 1 ? 'Complete' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutcomeWizard;